export {};

declare global {
    interface Array<T> {
		sum(): number
		each(callback: (value: any, index: number, array: any[]) => void): Array<T>
        range(length: number, start?: number): Array<T>
        flatDeep(iterateFn?: (value: any, index: number, array: any[]) => any): Array<T>
        deleteIndex(index: number): Array<T>
        deleteId(id: number): Array<T>
		unique(...props: string[]): Array<T>
		first: T
		last: T
		pluck(prop: string | number, key?: string | number): Array<any>
		chunk(chunkLenght: number): Array<Array<T>>
		syncWith(array: Array<T>, offset?: number): Array<T>
		crc32(): number
		iterator: ArrayIterator<T>
		_iterator: ArrayIterator<T>
    }
}

class ArrayIterator<T> {
	private _array: Array<T>;
	index: number = 0
	constructor(array: Array<T>) { this._array = array }
	get current(): T { return this._array[this.index]; }
	get isFirst(): boolean { return this.index === 0 }
	get isLast(): boolean { return this.index === (this._array.length - 1) }
	next(): T { return this.isLast ? this.current : this._array[++this.index] }
	previous(): T { return this.isFirst ? this.current : this._array[--this.index]; }
}

Object.defineProperty(Array.prototype, 'iterator', {
	enumerable: false,
    value: function() {
        return this._iterator ?? (this._iterator = new ArrayIterator(this));
    }
})

Object.defineProperty(Array.prototype, 'sum', {
	enumerable: false,
	value: function (): number {
		return this.reduce((a, b) => a + b, 0);
	}
})

Object.defineProperty(Array.prototype, 'each', {
	enumerable: false,
	value: function (callback: (value: any, index: number, array: any[]) => void): any[] {
		this.forEach(callback);
		return this;
	}
})

Object.defineProperty(Array.prototype, 'range', {
	enumerable: false,
	value: function (length: number, start: number = 0): any[] {
		return new Array(length).fill(null).map((_, index) => start + index);
	}
})

Object.defineProperty(Array.prototype, 'flatDeep', {
	enumerable: false,
	value: function (iterateFn?: (value: any, index?: number, array?: any[]) => any): any[] {
		return (iterateFn ? this.concat(this.map(iterateFn)) : this).reduce((a, v) => a.concat(Array.isArray(v) ? v.flatDeep(iterateFn) : v), []);
	}
});

Object.defineProperty(Array.prototype, 'deleteIndex', {
	enumerable: false,
	value: function (index: number): any[] {
		if(this[index]) {
			this.splice(index, 1);
			return this;
		}
		else return this;
	}
})

Object.defineProperty(Array.prototype, 'deleteId', {
	enumerable: false,
	value: function (...ids: number[]): any[] {
		let a: any[] = this;
		
		for (const id of ids) {
			const index = this.findIndex(i => i.id === +id);
			a = (index === -1 ? a : a.deleteIndex(index));
		}

		return a;
	}
})

Object.defineProperty(Array.prototype, 'unique', {
	enumerable: false,
	value: function (...props: string[]): any[] {
		return props.length > 0 ? 
			this.filter((v, i, a) => a.findIndex(t => props.map(f => t[f] === v[f]).reduce((p, c) => p && c, true)) === i):
			this.filter((v, i, a) => a.indexOf(v) === i);
	}
})

Object.defineProperty(Array.prototype, 'pluck', {
	enumerable: false,
	value: function (prop: string | number, key?: string | number): any[] {
		return key ? this.reduce((a, c) => {a[c[key]] = c[prop]; return a}, {}) : this.map(o => o && o[prop]);
	}
})

Object.defineProperty(Array.prototype, 'chunk', {
	enumerable: false,
	value: function (chunkLength: number): any[][] {
		return Array.from({ length: Math.ceil(this.length / chunkLength) }, (_, i) =>
				this.slice(i * chunkLength, i * chunkLength + chunkLength)
		);
	}
})

Object.defineProperty(Array.prototype, 'syncWith', {
	enumerable: false,
	value: function (array: any[], startAt: number = 0): any {

		const insertedCount: number = this.length;

		for(let index = 0; index < insertedCount; index++)
			if(array[startAt + index])
				array[startAt + index] = this[index];
			else 
				array.push(this[index]);

		array = array.splice(startAt + insertedCount, array.length - (startAt + insertedCount));

		return this;
	}
})

Object.defineProperty(Array.prototype, 'first', {
	enumerable: false,
    get: function() { return this[0]; },
	set: function(value) { this[0] = value; }
})

Object.defineProperty(Array.prototype, 'last', {
	enumerable: false,
    get: function() { return this[this.length - 1]; },
    set: function(value) { this[this.length - 1] = value; }
})

Object.defineProperty(Array.prototype, 'crc32', {
	enumerable: false,
	value: function (): number {
		return JSON.stringify(this).crc32();
	}
})